import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LoginInputSchema } from '@qualibot/shared';
import { login } from '@/lib/auth-api';
import { useAuthStore } from '@/stores/auth-store';
import { extractApiError } from '@/lib/api-client';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('admin123');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const token = useAuthStore((s) => s.token);
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const location = useLocation();

  if (token) {
    const from = (location.state as LocationState | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const parsed = LoginInputSchema.safeParse({ username, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid input');
      return;
    }

    setSubmitting(true);
    try {
      const { token, user } = await login(parsed.data);
      setSession(token, user);
      const from = (location.state as LocationState | null)?.from ?? '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="qb-card w-full max-w-[420px] mx-4 p-8">
        <div className="flex items-center gap-2.5 mb-7">
          <div className="w-9 h-9 rounded-lg bg-accent text-accent-text flex items-center justify-center text-base font-bold">
            Q
          </div>
          <div>
            <div className="text-[15px] font-semibold leading-tight -tracking-[0.01em]">
              Qualibot<span className="text-text-3">.pro</span>
            </div>
            <div className="text-[11.5px] text-text-3 mt-0.5">AI Agent Operating Console</div>
          </div>
        </div>

        <h1 className="text-xl font-semibold mb-1.5 -tracking-[0.01em]">Sign in</h1>
        <p className="text-[13.5px] text-text-2 mb-6">Use a seeded admin user to continue.</p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="u" className="text-[12px] font-semibold text-text-2">
              Username
            </label>
            <input
              id="u"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)]"
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="p" className="text-[12px] font-semibold text-text-2">
              Password
            </label>
            <input
              id="p"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)]"
              required
            />
          </div>

          {error && (
            <div
              className="text-[12.5px] px-3 py-2 rounded-md border"
              style={{
                background: 'var(--err-soft)',
                color: 'var(--err)',
                borderColor: 'transparent',
              }}
              role="alert"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="h-10 mt-1 rounded-[10px] bg-accent text-accent-text font-medium text-[13.5px] hover:bg-accent-strong disabled:opacity-60 transition-colors"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="mt-6 pt-5 border-t border-border text-[11.5px] text-text-3 leading-relaxed">
          MVP credentials —{' '}
          <span className="font-mono text-text-2">admin / admin123</span>,{' '}
          <span className="font-mono text-text-2">operator / operator123</span>,{' '}
          <span className="font-mono text-text-2">developer / developer123</span>
          <br />
          Future auth: Keycloak SSO placeholder.
        </div>
      </div>
    </div>
  );
}
